import React, { useState } from "react";
import { useTranslation, Trans } from "react-i18next";
import { SearchStatus } from "../../models/Interfaces";
import { immunizationsMultiMatchPdfURL, immunizationsNoMatchPdfURL } from "../../utils/data";
import { API_DOMAIN, PROTOCOL } from "../../globals";
import getIzProvider, { IzProvider } from "../../models/providers/IzProviders";
import { Modal } from "../../components/modals/Modal";
import { DocketAPIError, getAPIClient } from "../../apiClient";
import { errorLog } from "../../utils/logger";
import { ErrorModal } from "../../components/modals/ErrorModal";
import { izSearchAPIResultAtom, selectedSearchAtom } from "../../jotai/atoms";
import { useAtom } from "jotai";
import { useBranding } from "../../branding/useBranding";
import { Capacitor } from "@capacitor/core";
import { Dialog } from "@capacitor/dialog";

interface SearchResultsProps {
  onContinue: () => void;
  onDelete: () => void;
  isScenarioB: boolean;
  noMatch: boolean;
  multiMatch: boolean;
}

const jim = "/assets/images/Docket_Jim_Light.png";
const jimHC = "/assets/images/Docket_Jim_HC.png";

export function SearchResults({
  onContinue,
  onDelete,
  isScenarioB,
  noMatch,
  multiMatch,
}: SearchResultsProps) {
  const [existingSearch, setExistingSearch] = useAtom(selectedSearchAtom);
  const [_izSearchAPIResult, setIzSearchAPIResult] = useAtom(izSearchAPIResultAtom);
  const isWeb = Capacitor.getPlatform() === "web";

  const { t } = useTranslation();
  const branding = useBranding();
  const [deleteSearchModal, setDeleteSearchModal] = useState<boolean>(false);
  const [deleting, setDeleting] = useState<boolean>(false);
  const [showErrorModal, setShowErrorModal] = useState<string>();

  const providerKey = existingSearch?.izProviderKey;
  let izProvider: IzProvider | undefined;
  if (providerKey) {
    izProvider = getIzProvider(providerKey, t);
  }

  const navToHelpInfo = () => {
    const source =
      existingSearch?.status === SearchStatus.noMatch ||
      existingSearch?.status === SearchStatus.basicMatchNoContacts
        ? { uri: immunizationsNoMatchPdfURL, cache: true }
        : { uri: immunizationsMultiMatchPdfURL, cache: true };
    window.open(source.uri, "_blank", "noreferrer");
  };

  const handleDeleteSearchModal = async () => {
    if (isWeb) {
      setDeleteSearchModal(true);
    } else {
      const { value } = await Dialog.confirm({
        title: t("immunizations.alt_contacts_delete_heading"),
        message: t("immunizations.alt_contacts_delete_message"),
        okButtonTitle: t("generic.ok"),
        cancelButtonTitle: t("generic.cancel"),
      });
      if (value) {
        navToIncorrectContacts();
        await runDeleteSearch();
      }
    }
  };

  const runDeleteSearch = async () => {
    if (existingSearch === null) {
      return;
    }

    try {
      await getAPIClient().deleteIzSearch(existingSearch.uid, existingSearch.izProviderKey);
      const updatedSearches = await getAPIClient().getIzSearches(false);
      await setIzSearchAPIResult(updatedSearches.data);
      await setExistingSearch(null);
      onDelete();
    } catch (e: unknown) {
      errorLog(e as Error);
      if (e instanceof DocketAPIError) {
        setShowErrorModal(e.message);
      }
    }
  };

  const navToIncorrectContacts = () => {
    const url = `${PROTOCOL}://${API_DOMAIN}/api/immunizations/jurisdiction/${providerKey}/incorrect_contacts`;
    window.open(url, "_blank", "noreferrer");
  };

  const isScenarioBImage = isScenarioB || noMatch || multiMatch;

  function getSearchStatusImage(dark: boolean = false) {
    if (isScenarioBImage) {
      return izProvider?.scenarioBImagePath;
    }
    if (dark) {
      return jimHC;
    }
    return branding?.searchStatusImage || jim;
  }

  const getSearchStatusTranslationKey = () => {
    if (isScenarioB || noMatch) {
      return "immunizations.scenario_b_message";
    } else if (multiMatch) {
      return "immunizations.no_records_multi_match_message";
    }

    return "immunizations.search_queued_message";
  };

  function moreInfoCheck() {
    return multiMatch || existingSearch?.status === SearchStatus.noRecords;
  }

  function getButtonTitle() {
    if (isScenarioB || noMatch || multiMatch) {
      return t("immunizations.scenario_b_button_title", { context: providerKey });
    }
    return t("faq.faqs");
  }

  return (
    <div className="flex flex-col h-full max-md:gap-4">
      {deleteSearchModal && (
        <Modal
          id="delete-search-modal"
          open={deleteSearchModal}
          title={t("immunizations.alt_contacts_delete_heading")}
          onCloseModal={() => setDeleteSearchModal(false)}
          hasClose={true}
          actionButtons={
            <>
              <button
                className="btn info"
                aria-label="cancel"
                onClick={() => setDeleteSearchModal(false)}
                data-testid="cancel-btn"
              >
                {t("generic.cancel")}
              </button>
              <button
                className="btn"
                aria-label="ok"
                onClick={async () => {
                  setDeleting(true);
                  runDeleteSearch().finally(() => {
                    setDeleting(false);
                    setDeleteSearchModal(false);
                    navToIncorrectContacts();
                  });
                }}
              >
                {deleting ? (
                  <span className="loading loading-spinner loading-md"></span>
                ) : (
                  t("generic.ok")
                )}
              </button>
            </>
          }
        >
          <p>{t("immunizations.alt_contacts_delete_message")}</p>
        </Modal>
      )}
      {showErrorModal && (
        <ErrorModal title={t("generic.error")} onCloseModal={() => setShowErrorModal(undefined)}>
          <p>{showErrorModal}</p>
        </ErrorModal>
      )}
      {/* Scrollable content area */}
      <div className="flex-1 overflow-y-auto flex max-md:flex-col md:flex-row items-center md:items-stretch">
        <div className="md:hidden shrink min-h-0 flex justify-center items-center">
          <img
            src={getSearchStatusImage(false)}
            alt="Placeholder image"
            className="h-full dark:hidden object-contain"
          />
          <img
            src={getSearchStatusImage(true)}
            alt="Placeholder image"
            className="h-full not-dark:hidden object-contain"
          />
        </div>
        <div className="flex flex-col gap-2 flex-1 shrink-0 p-6 md:justify-start">
          <p className="" data-testid="search-status-message">
            <Trans i18nKey={getSearchStatusTranslationKey()} />
          </p>
          {/* Desktop buttons stay in content flow */}
          <div className="flex flex-col gap-2 items-center max-md:hidden">
            {moreInfoCheck() && (
              <button
                onClick={() => navToHelpInfo()}
                className="btn w-full"
                data-testid="multi-match-no-match-btn"
              >
                {t("immunizations.no_match_multi_match_no_match_button")}
              </button>
            )}
            {(isScenarioB || noMatch || multiMatch) && (
              <button onClick={handleDeleteSearchModal} className="btn btn-primary w-full">
                {getButtonTitle()}
              </button>
            )}
            {!(isScenarioB || noMatch || multiMatch) && (
              <button
                onClick={onContinue}
                className="btn btn-primary w-full"
                data-testid="continue-btn"
              >
                {t("generic.continue")}
              </button>
            )}
          </div>
        </div>
        <div className="md:flex-1 max-md:hidden flex items-center justify-center">
          <img
            src={getSearchStatusImage()}
            alt="Placeholder image"
            className="object-contain"
            data-testid="placeholder-desktop"
          />
        </div>
      </div>
      {/* Mobile buttons anchored at bottom */}
      <div className="flex-col flex gap-2 shrink-0 max-md:pb-8 md:hidden px-6">
        {moreInfoCheck() && (
          <button
            onClick={() => navToHelpInfo()}
            className="btn btn-lg w-full"
            data-testid="multi-match-no-match-btn"
          >
            {t("immunizations.no_match_multi_match_no_match_button")}
          </button>
        )}
        {(isScenarioB || noMatch || multiMatch) && (
          <button onClick={handleDeleteSearchModal} className="btn btn-lg btn-primary w-full">
            {getButtonTitle()}
          </button>
        )}
        {!(isScenarioB || noMatch || multiMatch) && (
          <button
            onClick={onContinue}
            className="btn btn-lg btn-primary w-full"
            data-testid="continue-btn-mobile"
          >
            {t("generic.continue")}
          </button>
        )}
      </div>
    </div>
  );
}

export default SearchResults;
